import { padNumber } from '@apeiron/common'
import { useMessageContext } from '@apeiron/temp'
import { KeyboardArrowUp as KeyboardArrowUpIcon } from '@mui/icons-material'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  styled,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material'
import RentalSettingDialog, {
  RentalType
} from '@src/components/orbitalTrack/rentalSetting/common/RentalSettingDialog'
import TrackDataGrid from '@src/components/orbitalTrack/rentalSetting/common/TrackDataGrid'
import ChangeGroupNameButtonWithPopup from '@src/components/orbitalTrack/rentalSetting/inviteRental/ChangeGroupNameButtonWithPopup'
import ApeironBorderButton from '@src/components/share/apeiron/ApeironBorderButton'
import ClickToShowToolTip from '@src/components/share/generic/ClickToShowToolTip'
import ENV_CONFIG from '@src/config'
import { useOrbitalTrackAttachContext } from '@src/contexts/orbitalTrack/OrbitalTrackAttachContext'
import * as R from 'ramda'
import {
  FC,
  MouseEvent,
  SyntheticEvent,
  useCallback,
  useMemo,
  useState
} from 'react'
import { Trans, useTranslation } from 'react-i18next'

const Container = styled(Accordion)`
  background: linear-gradient(90deg, #1a1a1a 0%, #000000 100%);
  display: flex;
  flex-direction: column;
  width: 100%;
  ${props => props.theme.breakpoints.up('res480')} {
    padding: 15px 20px;
  }
`

const Head = styled(AccordionSummary)`
  ${props => props.theme.breakpoints.up('res1024')} {
    flex-direction: row-reverse;

    & .MuiAccordionSummary-expandIconWrapper {
      transform: rotate(90deg);
    }
    & .MuiAccordionSummary-expandIconWrapper.Mui-expanded {
      transform: rotate(180deg);
    }
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    .MuiAccordionSummary-expandIconWrapper {
      position: absolute;
      right: 10px;
      top: -5px;
    }
  }
  .MuiAccordionSummary-content {
    align-items: center;
    display: flex;
    ${props => props.theme.breakpoints.up('res1024')} {
      /* justify-content: space-between; */
    }
    ${props => props.theme.breakpoints.down('res1024')} {
      align-items: flex-start;
      flex-direction: column;
      gap: 12px;
    }
  }

  .MuiAccordionSummary-content.Mui-expanded {
    margin: 12px 0px;
  }
`

const LeftContainer = styled(Box)`
  width: 40%;
  display: flex;
  column-gap: 10px;
  align-items: center;
  ${props => props.theme.breakpoints.down('res1024')} {
    width: 85%;
  }
`

const RightContaner = styled(Box)`
  align-items: center;
  display: flex;
  justify-content: space-between;
  width: 60%;

  ${props => props.theme.breakpoints.down('res1024')} {
    width: 100%;
  }
`

const Title = styled(Typography)`
  color: white;
  display: inline;
  font-weight: 500;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  ${props => props.theme.breakpoints.up('res480')} {
    font-size: 20px;
    line-height: 20px;
  }
  ${props => props.theme.breakpoints.down('res480')} {
    font-size: 14px;
    line-height: 16px;
  }
`

const WhiteTitle = styled(Title)`
  font-weight: 700;
`

const OrangeTitle = styled(Title)`
  color: #ffbf36;
  font-weight: 700;
`

const EditButton = styled(ApeironBorderButton)`
  border: 2px solid #818181;
  font-weight: 300;
  ${props => props.theme.breakpoints.down('res480')} {
    font-size: 14px;
    padding: 0px 10px;
  }
`

const StyledIcon = styled(KeyboardArrowUpIcon)`
  color: white;
  font-size: 56px;
`

const PriorityText = styled(Typography)`
  color: black;
  background-color: #00d1ff;
  padding: 3px;
  border-radius: 2px;
  white-space: nowrap;
  ${props => props.theme.breakpoints.down('res480')} {
    font-size: 14px;
    padding: 0px 10px;
  }
`

const InviteLinkText = styled(Typography)`
  color: #ffb10b;
  text-decoration-line: underline;
  font-weight: 700;
  ${props => props.theme.breakpoints.down('res480')} {
    font-size: 14px;
  }
`

const InviteSettingButtonsContainer = styled(Box)`
  display: inline-flex;
  gap: 10px;
  align-items: center;
`

const OccupiedCount = styled(Box)`
  text-align: center;
`

const RentalEditRow: FC<Props> = props => {
  const {
    groupId,
    rentalType,
    displayedPriority,
    onPriorityEditClick,
    defaultExpanded = false
  } = props

  const { t } = useTranslation()

  const { trackDetail, refetchStar } = useOrbitalTrackAttachContext()

  const domain = ENV_CONFIG.WEB_URL.MARKETPLACE

  const { setSnackInfo } = useMessageContext()

  const [expand, setExpand] = useState<boolean>(defaultExpanded)

  const [open, setOpen] = useState<boolean>(false)

  const theme = useTheme()

  const under768 = useMediaQuery(theme.breakpoints.down('res768'))

  const handleOnExpand = useCallback((_: SyntheticEvent, expanded: boolean) => {
    setExpand(expanded)
  }, [])

  const handleOnClick = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation()
      setOpen(true)
    },
    [setOpen]
  )

  const handleOnClose = useCallback(
    (success: boolean) => {
      setOpen(false)
      if (success) {
        refetchStar()
      }
    },
    [refetchStar]
  )

  const handleOnLinkClick = useCallback(() => {
    navigator.clipboard.writeText(
      domain + `star/starAttachAndTrack/?starId=${trackDetail?.star?.id}`
    )
  }, [domain, setSnackInfo, t, trackDetail?.star?.id])

  const setting = useMemo(() => {
    if (rentalType === RentalType.INVITE) {
      return R.find(
        R.propEq(groupId, 'inviteGroupId'),
        trackDetail?.inviteSettings || []
      )
    } else {
      return trackDetail?.openSetting
    }
  }, [
    groupId,
    trackDetail?.inviteSettings,
    trackDetail?.openSetting,
    rentalType
  ])

  const occupied = setting?.occupiedTrack || 0

  const opened = setting?.totalTrack || 0

  const getTitle = () => {
    if (rentalType === RentalType.INVITE) {
      return setting?.groupName
    } else {
      return t('godiverse.orbital_track.public_track')
    }
  }

  const handleOnPriorityEditClick = useCallback(
    (e: MouseEvent) => {
      if (!onPriorityEditClick) return

      e.stopPropagation()
      onPriorityEditClick()
    },
    [onPriorityEditClick]
  )

  return (
    <Box>
      <Container
        defaultExpanded={rentalType === RentalType.PUBLIC}
        onChange={handleOnExpand}
      >
        <Head expandIcon={<StyledIcon />}>
          <LeftContainer>
            <Title>{getTitle()}</Title>
            {rentalType === RentalType.INVITE && (
              <InviteSettingButtonsContainer>
                <ChangeGroupNameButtonWithPopup setting={setting} />
                <PriorityText onClick={handleOnPriorityEditClick}>
                  {under768
                    ? padNumber(displayedPriority, 2)
                    : t('godiverse.orbital_track.priority', {
                        number: displayedPriority || 0
                      })}
                </PriorityText>
                {' - '}
                <ClickToShowToolTip
                  title={t('godiverse.orbital_track.invite_link_copied')}
                  disableEventPropagation
                >
                  <InviteLinkText onClick={handleOnLinkClick}>
                    {t('godiverse.orbital_track.get_invite_link')}
                  </InviteLinkText>
                </ClickToShowToolTip>
              </InviteSettingButtonsContainer>
            )}
          </LeftContainer>
          <RightContaner>
            <Title>
              <Trans
                components={{
                  orange: <OrangeTitle />,
                  white: <WhiteTitle />
                }}
              >
                {!under768
                  ? t('godiverse.orbital_track.tracks_occupied_detail', {
                      count: occupied,
                      total: opened
                    })
                  : t('godiverse.orbital_track.tracks_occupied_header')}
              </Trans>
              {under768 && (
                <OccupiedCount>
                  <OrangeTitle>{occupied}</OrangeTitle>/
                  <WhiteTitle>{opened}</WhiteTitle>
                </OccupiedCount>
              )}
            </Title>
            <EditButton
              disabled={trackDetail?.star?.sale}
              disableReason={t('orbit.block_on_star_listing')}
              onClick={handleOnClick}
            >
              {t('godiverse.orbital_track.edit_rental')}
            </EditButton>
          </RightContaner>
        </Head>
        <AccordionDetails>
          {expand && <TrackDataGrid inviteGroupId={groupId} />}
        </AccordionDetails>
        <RentalSettingDialog
          open={open}
          groupId={groupId}
          rentalType={rentalType}
          onClose={handleOnClose}
        />
      </Container>
    </Box>
  )
}

type Props = {
  groupId: string
  rentalType: RentalType
  displayedPriority?: number
  defaultExpanded?: boolean
  onPriorityEditClick?: () => void
}

export default RentalEditRow
